import Graph from 'graphology';
export type TimelineCalendarRow<T = unknown> = {
  id: string;
  title: string;
  properties?: T;
};

export type TimelineCalendarEvent<T = unknown> = {
  id: string;
  title: string;
  start: number;
  end: number;
  row: string;
  segment?: string;
  properties?: T;
};

export type TimelineCalendarColumn<T = unknown> = {
  id: string;
  title: string;
  numericValue: number;
  properties?: T;
};

export type ColumnGenerator<T = unknown> = (
  offset: number,
) => TimelineCalendarColumn<T>;

export const findStartColumn = (
  columns: TimelineCalendarColumn[],
  eventStart: number,
): [number, TimelineCalendarColumn | null] | null => {
  const sortedColumns = [...columns].sort(
    (a, b) => a.numericValue - b.numericValue,
  );
  const firstColumn = sortedColumns[0];
  if (eventStart < firstColumn?.numericValue) {
    return [-1, null];
  }
  for (let index = 0; index < sortedColumns.length; index++) {
    const column = sortedColumns[index];
    if (column.numericValue >= eventStart) {
      return [index - 1, column];
    }
  }
  return null;
};

export const findEndColumn = (
  columns: TimelineCalendarColumn[],
  eventEnd: number,
): [number, TimelineCalendarColumn | null] | null => {
  const sortedColumns = [...columns].sort(
    (a, b) => a.numericValue - b.numericValue,
  );
  const lastColumn = sortedColumns[sortedColumns.length - 1];
  if (eventEnd > lastColumn?.numericValue) {
    return [sortedColumns.length, null];
  }
  for (let index = 0; index < sortedColumns.length; index++) {
    const column = sortedColumns[index];
    if (column.numericValue >= eventEnd) {
      return [index, column];
    }
  }
  return null;
};

const checkValueOverlap = (
  start1: number,
  end1: number,
  start2: number,
  end2: number,
): boolean => {
  return (
    (start1 < end2 && end1 > start2) ||
    (start2 < end1 && end2 > start1) ||
    (start1 <= start2 && end1 >= end2) ||
    (start2 <= start1 && end2 >= end1)
  );
};

export const generateRowGraphs = (
  events: TimelineCalendarEvent[],
  columns: TimelineCalendarColumn[],
) => {
  const rowEvents: Record<string, TimelineCalendarEvent[]> = events.reduce(
    (acc, event) => {
      if (!acc[event.row]) {
        acc[event.row] = [];
      }
      acc[event.row].push(event);
      return acc;
    },
    {} as Record<string, TimelineCalendarEvent[]>,
  );

  const rowGraphs: Record<
    string,
    Graph<TimelineCalendarEvent>
  > = Object.entries(rowEvents).reduce((acc, [rowId, events]) => {
    const graph = new Graph<TimelineCalendarEvent>();
    events.forEach((event) => {
      try {
        graph.addNode(event.id, event);
      } catch (e) {
        console.warn(`Failed to add event with id ${event.id} to graph:`, e);
      }
    });
    acc[rowId] = graph;
    return acc;
  }, {} as Record<string, Graph<TimelineCalendarEvent>>);

  Object.values(rowGraphs).forEach((graph) => {
    // add edges for nodes that overlap regarding start and end times
    graph.forEachNode((nodeKey, nodeData) => {
      graph.forEachNode((otherNodeKey, otherNodeData) => {
        if (nodeKey !== otherNodeKey) {
          const [startColumnInfo] = findStartColumn(
            columns,
            nodeData.start,
          ) ?? [-1, null];
          const [endColumnInfo] = findEndColumn(columns, nodeData.end) ?? [
            -1,
            null,
          ];
          const [otherStartColumnInfo] = findStartColumn(
            columns,
            otherNodeData.start,
          ) ?? [-1, null];
          const [otherEndColumnInfo] = findEndColumn(
            columns,
            otherNodeData.end,
          ) ?? [-1, null];
          if (
            checkValueOverlap(
              startColumnInfo,
              endColumnInfo,
              otherStartColumnInfo,
              otherEndColumnInfo,
            ) ||
            nodeData.segment !== otherNodeData.segment
          ) {
            graph.addEdge(nodeKey, otherNodeKey);
          }
        }
      });
    });
  });

  return rowGraphs;
};

export const generateGraphColors = (
  graph: Graph<TimelineCalendarEvent>,
): [number, Record<string, number>] => {
  const colors: Record<string, number> = {};
  let maxColor = 0;

  graph
    .nodes()
    // .sort((first, second) => {
    //   const firstData = graph.getNodeAttributes(first);
    //   const secondData = graph.getNodeAttributes(second);
    //   return firstData.start - secondData.start;
    // })
    .forEach((node) => {
      const neighborColors = new Set(
        graph
          .neighbors(node)
          .map((neighbor) => colors[neighbor])
          .filter((color) => color !== undefined),
      );

      let color = 0;
      while (neighborColors.has(color)) {
        color++;
      }
      colors[node] = color;
      if (color > maxColor) {
        maxColor = color;
      }
    });

  const colorCount = maxColor + 1;

  return [colorCount, colors];
};
